import { Box, HBox, VBox } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { Typo, TypoError, TypoMedium } from "../text/Typo";
import { RecursiveCSS } from "../theme/GlobalTheme";

const getDetailsText = (details: any): string => {
	if (details === undefined || details === null) return "";
	if (typeof details === "string" || typeof details === "number" || typeof details === "boolean") return String(details);

	if (Array.isArray(details) && details.every((val: any) => ["string", "number", "boolean"].includes(typeof val))) {
		return details.join(", ");
	}

	if (typeof details === "object") {
		try {
			const seen = new WeakSet();
			return JSON.stringify(details, (_key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) return "[Circular]";
					seen.add(value);
				}
				return value;
			});
		} catch {
			return String(details);
		}
	}

	return String(details);
};

const ErrorDetailsList = ({ values, hideItemTitles = false }: { values: unknown[]; hideItemTitles?: boolean }) => (
	<VBox gap="6px">
		{values.map((value, index) => {
			const isObject = typeof value === "object" && value !== null && !Array.isArray(value);
			const detail = isObject ? (value as Record<string, unknown>) : null;
			const message = detail && detail.message !== undefined ? getDetailsText(detail.message) : getDetailsText(value);
			const description = detail && detail.details !== undefined ? getDetailsText(detail.details) : "";
			const primaryText = hideItemTitles ? description || message : message;
			const secondaryText = hideItemTitles ? "" : description;

			return (
				<HBox key={index} gap="10px" sx={{ alignItems: "flex-start", minWidth: 0 }}>
					<Box sx={{ backgroundColor: "#e91e63", width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, marginTop: "6px" }} />
					<VBox gap="2px" sx={{ minWidth: 0 }}>
						<TypoMedium sx={{ lineHeight: "18px", overflowWrap: "anywhere" }}>{primaryText}</TypoMedium>
						{secondaryText && (
							<Typo sx={{ fontSize: "13px", lineHeight: "18px", opacity: 0.8, overflowWrap: "anywhere" }}>{secondaryText}</Typo>
						)}
					</VBox>
				</HBox>
			);
		})}
	</VBox>
);

export const Error = ({ error, sx }: { error?: any; sx?: RecursiveCSS }) => {
	let displayError = null;

	if (error) {
		if (typeof error === "object" && "code" in error && "message" in error) {
			displayError = error;
		} else {
			if (typeof error === "string" || typeof error === "number" || typeof error === "boolean") displayError = { code: 0, message: error };
			else {
				if (typeof error === "object" && ("code" in error || "message" in error)) {
					displayError = error.error;
				} else {
					try {
						const content = JSON.stringify(error);
						displayError = { code: 1, message: content };
					} catch (e) {
						displayError = { code: 2, message: "unknown error" };
					}
				}
			}
		}
	}

	if (!displayError) return null;

	if (displayError.code <= -100) return null;

	const detailsList = Array.isArray(displayError.details) ? displayError.details : null;
	const stackList = Array.isArray(displayError.stack) ? displayError.stack : null;

	return (
		<Box
			sx={{
				border: "1px dashed red",
				padding: "10px",
				borderRadius: "6px",
				backgroundColor: "rgba(255,0,0,0.1)",
				...sx,
			}}>
			<TypoError>{displayError.message}</TypoError>
			{displayError.details && (
				<>
					<Spacer size="6px" />
					{detailsList ? <ErrorDetailsList values={detailsList} /> : <TypoMedium block>{getDetailsText(displayError.details)}</TypoMedium>}
				</>
			)}
			{stackList && stackList.length > 0 && (
				<>
					<Spacer size="6px" />
					<ErrorDetailsList values={stackList} hideItemTitles />
				</>
			)}
		</Box>
	);
};
